var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { generateScriptPathFromModuleId, getModuleIdFromWidget, hasValue, loadScriptFileAsync, lookupComponent, trackAnalytics, tryParseJSON } from '@utils';
import { useAddOns } from './useAddOns';
import { UXPContext } from '@core/context/Context';
import { FontAwesomeIconWrapper } from '@icon/fontawesome/FontAwesomeIconWrapper';
import useToast from '@notifications/Toast/useToast';
import Modal from '../../widget/popups/Modal';
import Button from '@widget/buttons/Button';
import { Loading } from '@widget/loaders';
import { executeConfig } from '@core/context/useExecuteRequest';
const InstallAddOn = (props) => {
    const { show, onClose, widget, onInstallationComplete, onConfigurationComplete } = props;
    const context = React.useContext(UXPContext);
    const [configUI, setConfigUI] = React.useState(null);
    const [hasScriptLoaded, setHasScriptLoaded] = React.useState(false);
    const [status, setStatus] = React.useState('loading');
    const [hasError, setHasError] = React.useState(false);
    const [counter, setCounter] = React.useState(0);
    const addOns = useAddOns();
    const toast = useToast();
    const moduleId = widget ? getModuleIdFromWidget(widget) : null;
    const addOnName = addOns.getAddOnName(widget, moduleId);
    React.useEffect(() => {
        setStatus('loading');
        setHasError(false);
        setConfigUI(null);
        setHasScriptLoaded(false);
    }, []);
    React.useEffect(() => {
        setStatus('loading');
        setHasError(false);
        if (addOns.isAddOn(moduleId)) {
            const { installed, configured } = addOns.isAddOnInstalledAndConfigured(moduleId);
            const needToConfigure = addOns.isConfigurationRequired(moduleId);
            if (installed) {
                if (!configured && needToConfigure) {
                    setStatus('configuring');
                }
                else {
                    setStatus('done');
                }
            }
            else {
                setStatus('installing');
            }
        }
    }, [widget]);
    React.useEffect(() => {
        if (status === 'installing') {
            installUseCase();
        }
        else if (status == 'configuring') {
            getUseCaseDetails();
        }
    }, [status]);
    React.useEffect(() => {
        if (hasValue(configUI) && widget) {
            loadScriptFileAsync(generateScriptPathFromModuleId(moduleId), moduleId, true)
                .then(() => {
                setHasScriptLoaded(true);
                setCounter(prev => (prev += 1));
            });
        }
    }, [configUI, widget]);
    function installUseCase() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d;
            if (!moduleId)
                return;
            const result = yield executeConfig(context, {
                type: 'service',
                app: 'Lucy',
                service: 'InstallUseCase',
                parameters: { useCaseId: moduleId },
                options: { json: true, cancelPrevious: true, executeImmidiately: true, key: moduleId }
            });
            if (result.error || ((_b = (_a = result.data) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.error)) {
                console.error('Unable to install the addon. Error: ', result.error || ((_d = (_c = result.data) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.error));
                toast.error(context.$L('uxp-core.addon.unable-to-install'));
                return;
            }
            setStatus('configuring');
            trackAnalytics(context, 'Addon Installed', { id: moduleId, name: addOnName });
            if (onInstallationComplete && widget) {
                onInstallationComplete(widget);
            }
        });
    }
    function getUseCaseDetails() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!moduleId)
                return;
            const result = yield executeConfig(context, {
                type: 'service',
                app: 'Lucy',
                service: 'InstalledUseCase:DetailsByID',
                parameters: { ID: moduleId },
                options: { json: true }
            });
            if (result.error) {
                console.error('Unable to get the usecase details. Error: ', result.error);
                setHasError(true);
                return;
            }
            const details = result.data;
            if (!details || !details[0]) {
                // No package record — addon has no configuration UI, proceed as configured
                setStatus('done');
                if (onConfigurationComplete && widget)
                    onConfigurationComplete(widget);
                return;
            }
            if (details[0].error) {
                console.error('Unable to get the usecase details. Error: ', details[0].error);
                setHasError(true);
                return;
            }
            const packageDetails = tryParseJSON(details[0].Package, {});
            const configUI = packageDetails === null || packageDetails === void 0 ? void 0 : packageDetails.configUI;
            if (hasValue(configUI === null || configUI === void 0 ? void 0 : configUI.default)) {
                setConfigUI(configUI.default);
            }
            else {
                setStatus('done');
                if (onConfigurationComplete && widget) {
                    onConfigurationComplete(widget);
                }
            }
        });
    }
    function renderUI() {
        if (!configUI)
            return;
        let Item = lookupComponent(`${moduleId}/${configUI}`.toLowerCase());
        if (Item && Item.component) {
            let Component = Item.component;
            let _props = {};
            if (Item.props) {
                _props = Item.props;
            }
            _props.uxpContext = context;
            _props.packageUrl = `${addOns.AddOnS3BaseUrl}/${moduleId}/latest`;
            _props.onClose = () => {
                trackAnalytics(context, 'Addon Configured', { id: moduleId, name: addOnName });
                if (onConfigurationComplete && widget) {
                    onConfigurationComplete(widget);
                }
                onClose();
            };
            return _jsx(Component, Object.assign({}, _props));
        }
        return (_jsx("div", { className: "uxpcore_addon-install__configure-loading", children: _jsx(Loading, {}) }));
    }
    return (_jsx(Modal, { show: props.show, onClose: onClose, title: `${status === 'installing' ? context.$L('uxp-core.text.install') : context.$L('uxp-core.text.configure')} ${addOnName}`, className: `uxpcore_addon-install uxpcore_addon-install--${status}`, children: _jsxs("div", { className: `uxpcore_addon-install__content${status === 'configuring' ? ' uxpcore_addon-install__content--configure' : ''}`, style: status !== 'configuring' ? { backgroundImage: `url(${moduleId ? addOns.getThunmbNailImagePath(moduleId) : ''})` } : undefined, children: [status === 'loading' && (_jsx("div", { className: "uxpcore_addon-install__loading", children: _jsx(Loading, {}) })), status === 'installing' && (_jsxs("div", { className: "uxpcore_addon-install__installing", children: [_jsxs("div", { className: "uxpcore_addon-install__anim", children: [_jsx("div", { className: "uxpcore_addon-install__anim-ring" }), _jsx("div", { className: "uxpcore_addon-install__anim-ring uxpcore_addon-install__anim-ring--delay" }), _jsx("div", { className: "uxpcore_addon-install__anim-icon", children: _jsx(FontAwesomeIconWrapper, { icon: ['fas', 'cloud-download'] }) })] }), _jsx("div", { className: "uxpcore_addon-install__message", children: _jsx("span", { dangerouslySetInnerHTML: { __html: context.$L('uxp-core.addon.installing-addon', { addOnName }) } }) })] })), status === 'configuring' && (_jsx("div", { className: "uxpcore_addon-install__configure", children: renderUI() })), status === 'done' && (_jsxs("div", { className: "uxpcore_addon-install__done", children: [_jsx("div", { className: "uxpcore_addon-install__done-icon", children: _jsx(FontAwesomeIconWrapper, { icon: ['fas', 'check-circle'] }) }), _jsx("div", { className: "uxpcore_addon-install__message", children: _jsx("span", { dangerouslySetInnerHTML: { __html: context.$L('uxp-core.addon.addon-installed', { addOnName }) } }) }), _jsx("div", { className: "uxpcore_addon-install__actions", children: _jsx(Button, { title: context.$L('uxp-core.text.close'), onClick: onClose }) })] }))] }) }));
};
export default InstallAddOn;
